import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { Ambulance, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-lg border border-slate-200 p-8" data-testid="forgot-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-md bg-[#0A192F] flex items-center justify-center">
            <Ambulance className="text-white" size={20} />
          </div>
          <div>
            <div className="font-display font-semibold text-lg">{BRAND}</div>
            <div className="text-xs text-slate-500">Password reset</div>
          </div>
        </div>

        {sent ? (
          <div data-testid="forgot-sent">
            <div className="flex items-center gap-2 text-emerald-700 font-medium"><MailCheck size={18} /> Check your email</div>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              If an account exists for <strong>{email}</strong>, a reset link is on its way. It works for 60 minutes. If nothing arrives, check your spam folder or ask your agency admin.
            </p>
            <Link to="/login" className="inline-block mt-6 text-sm text-[#005E82] font-medium hover:underline">Back to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
            <p className="text-sm text-slate-600 leading-relaxed">Enter the email you sign in with and we will send you a link to choose a new password.</p>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Email</label>
              <input
                data-testid="forgot-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
                required
              />
            </div>
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="forgot-error">{err}</div>}
            <button type="submit" disabled={busy} className="w-full btn-primary disabled:opacity-60" data-testid="forgot-submit">
              {busy ? "Sending..." : "Send reset link"}
            </button>
            <div className="text-sm text-slate-500">
              Remembered it? <Link to="/login" className="text-[#005E82] font-medium hover:underline">Back to sign in</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
